import { type FC } from "react";

import {
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "../interaction-mode/helpers";

import { PointsEditor } from "components/materials/common/PointsEditor";
import { t } from "i18n/i18n";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

interface PageMaxPointsProps {
  disabled?: boolean;
  hideIcon?: boolean;
  isPageOverviewHeader?: boolean;
  onSave: (points: number) => void;
  pageId: string;
  readOnly?: boolean;
  tooltipText?: string;
}

const parseInput = (input: string) => {
  // Empty input is not valid, but also not an error.
  if (!input) {
    return {};
  }

  // Validate format and range.
  const value = parseFloat(input);
  if (isNaN(value) || value < 0) {
    return { error: t("error.max_points_less_than_zero") };
  }

  // After passing the range validation check the precision limit.
  if (!Number.isInteger(value)) {
    return {
      error: t("error.whole_number_required"),
    };
  }

  return { value };
};

export const PageMaxPoints: FC<PageMaxPointsProps> = ({
  disabled = false,
  hideIcon = false,
  isPageOverviewHeader = false,
  onSave,
  pageId,
  readOnly = false,
  tooltipText = "",
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["max_points"]);

  if (!page) return null;

  const buttonLabel = t("rubric_card.assessment_points", { count: page.max_points });

  const buttonLabelShort = isPageOverviewHeader
    ? t("rubric_card.assessment_points_short", { count: page.max_points })
    : undefined;

  const containerQueryClassNameHide = isPageOverviewHeader
    ? PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE
    : undefined;

  const containerQueryClassNameShow = isPageOverviewHeader
    ? PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW
    : undefined;

  return (
    <PointsEditor
      buttonLabel={buttonLabel}
      buttonLabelShort={buttonLabelShort}
      containerQueryClassNameHide={containerQueryClassNameHide}
      containerQueryClassNameShow={containerQueryClassNameShow}
      disabled={disabled}
      eventName="Page - Update points"
      hideIcon={hideIcon}
      initialPoints={page.max_points}
      inputTitle={t("tooltip.configure_points")}
      pageId={pageId}
      parseInput={parseInput}
      readOnly={readOnly}
      tooltipText={tooltipText}
      onSave={onSave}
    />
  );
};
